import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { stripComponents } from 'features/libra/ontology/ontologyFieldTypes'
import { ticketOntologyFieldsOf } from 'features/libra/ontology/useOntologyFields'
import { useTicketsSignoffEntries } from 'features/libra/signoff/signoffsApi'
import { buildNiiReportContent } from 'features/libra/stepComponents/niiReport/content/buildNiiReportContent'
import { getNiiReportConfig } from 'features/libra/stepComponents/niiReport/niiReportApi'
import {
  parseZonesFromFieldValues,
  type ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import { useRolodexEntries } from 'features/rolodex'
import uniq from 'lodash/uniq'
import { useCallback, useMemo, useState } from 'react'
import { RolodexEntryType } from 'types'
import { downloadBlob } from 'utils/download'

import {
  buildNiiSheetRows,
  findMissingTicketIdentity,
  type NiiSheetTicket,
} from './buildNiiSheetRows'
import {
  buildNiiSheetFilename,
  buildNiiSheetWorkbook,
} from './buildNiiSheetWorkbook'
import {
  applyNiiSheetSelection,
  type NiiSheetColumns,
  type NiiSheetSelection,
} from './niiSheetSelection'

export interface NiiSheetExportSummary {
  exportedCount: number
  /** Tickets whose workflow turned out to have no usable NII Report step. */
  skipped: ReadonlyArray<LibraTicket>
}

/**
 * Exports every given ticket's NII report content as one spreadsheet.
 *
 * Reads the same `buildNiiReportContent` the PDF export renders from, so the
 * numbers in the sheet cannot drift from the numbers in the report.
 *
 * Much lighter than `useBulkNiiPdfExport` because a sheet needs no zone
 * images: there are no Rolodex image lookups, no signed URLs, no canvas
 * redraw, and no `@react-pdf` render. Content building is synchronous and one
 * file is produced, so this needs none of that hook's concurrency limiting or
 * download staggering.
 *
 * Caller-agnostic by design: it takes plain tickets plus a `workflowMap`
 * covering their `workflowId`s, so the Explorer panel can drive it from a
 * drilldown scope with no Libra ticket provider in the tree.
 */
export function useBulkNiiSheetExport(
  tickets: ReadonlyArray<LibraTicket>,
  workflowMap: ReadonlyMap<string, LibraWorkflow>,
  organizationId: string,
  selection: NiiSheetSelection
): {
  generateAndDownload: () => Promise<NiiSheetExportSummary>
  isGenerating: boolean
  /** True until sign-offs load; exporting earlier would leave them blank. */
  isLoading: boolean
  /** Every column the scope can produce, for the panel's config drawer. */
  columnsBySheet: NiiSheetColumns
  /** Ticket titles that will export a blank Asset ID or Site cell. */
  missingIdentity: {
    assetId: ReadonlyArray<string>
    site: ReadonlyArray<string>
  }
} {
  const [isGenerating, setIsGenerating] = useState(false)

  const unitIds = useMemo(
    () =>
      uniq(
        tickets
          .map((ticket) => ticket.tags.unit)
          .filter((id): id is string => !!id)
      ),
    [tickets]
  )
  const { units, isLoading: unitsLoading } = useRolodexEntries(
    useMemo(
      () =>
        unitIds.length > 0
          ? {
              type: RolodexEntryType.UNIT,
              ids: unitIds,
              tags: { organization: organizationId },
            }
          : undefined,
      [unitIds, organizationId]
    )
  )
  const unitsById = useMemo(
    () => new Map(units.map((unit) => [unit.id, unit])),
    [units]
  )

  const siteIds = useMemo(
    () =>
      uniq(
        units.map((unit) => unit.tags.site).filter((id): id is string => !!id)
      ),
    [units]
  )
  const { sites, isLoading: sitesLoading } = useRolodexEntries(
    useMemo(
      () =>
        siteIds.length > 0
          ? { type: RolodexEntryType.SITE, ids: siteIds }
          : undefined,
      [siteIds]
    )
  )
  const sitesById = useMemo(
    () => new Map(sites.map((site) => [site.id, site])),
    [sites]
  )

  const ticketIds = useMemo(() => tickets.map(({ id }) => id), [tickets])
  const { signoffsByTicketId, isLoading: signoffsLoading } =
    useTicketsSignoffEntries(ticketIds)

  // Zones per ticket, using each ticket's own workflow-configured field.
  const zonesByTicketId = useMemo(() => {
    const map = new Map<string, ReadonlyArray<ZoneParams>>()
    tickets.forEach((ticket) => {
      const workflow = workflowMap.get(ticket.workflowId)
      const zoneFieldName =
        getNiiReportConfig(workflow?.steps)?.zoneFieldName ?? 'zones'
      const { zones } = parseZonesFromFieldValues(
        ticket.fieldValues,
        zoneFieldName
      )
      map.set(ticket.id, zones)
    })
    return map
  }, [tickets, workflowMap])

  const buildSheetTicket = useCallback(
    (ticket: LibraTicket): NiiSheetTicket | undefined => {
      const workflow = workflowMap.get(ticket.workflowId)
      const reportConfig = getNiiReportConfig(workflow?.steps)
      if (!workflow || !reportConfig) return undefined

      const unit = ticket.tags.unit
        ? unitsById.get(ticket.tags.unit)
        : undefined
      const site = unit?.tags.site ? sitesById.get(unit.tags.site) : undefined

      // Same treatment as the PDF path: export reads ticket-root values, not
      // the per-component subtree.
      const fieldValues = stripComponents(ticket.fieldValues)

      const sections = buildNiiReportContent(workflow.steps, {
        fieldValues,
        ontologyFields: ticketOntologyFieldsOf(
          workflow.globalContext.ontologyFields
        ),
        zones: zonesByTicketId.get(ticket.id) ?? [],
        // A sheet has nowhere to put an image, and resolving them is the
        // expensive half of the PDF export.
        zoneImages: [],
        reportFields: reportConfig.fields,
        signoffConfig: workflow.signoffConfig,
        signoffs: signoffsByTicketId.get(ticket.id) ?? {},
        lifecycle: {
          config: workflow.lifecycleConfig,
          ticketStatus: ticket.ticketStatus,
          terminalTransitionBy: ticket.terminalTransitionBy,
          terminalTransitionAt: ticket.terminalTransitionAt,
          submittedForReviewBy: ticket.submittedForReviewBy,
          submittedForReviewAt: ticket.submittedForReviewAt,
        },
      })

      return {
        ticketId: ticket.id,
        ticketTitle: ticket.title,
        meta: {
          siteName: site?.data.name as string | undefined,
          assetName: unit?.data.name as string | undefined,
          analysisDate: unwrapFieldValue<string>(
            fieldValues[reportConfig.fields.analysisDate]
          ),
          analysisVersion: ticket.version,
        },
        sections,
      }
    },
    [workflowMap, unitsById, sitesById, zonesByTicketId, signoffsByTicketId]
  )

  /**
   * Rows are built up front rather than on click so the config drawer can
   * offer the real column list. Content building is pure and synchronous, and
   * this only re-runs when the drilldown scope changes.
   */
  const { rows, sheetTickets, skipped } = useMemo(() => {
    const included: NiiSheetTicket[] = []
    const dropped: LibraTicket[] = []
    tickets.forEach((ticket) => {
      const sheetTicket = buildSheetTicket(ticket)
      if (sheetTicket) included.push(sheetTicket)
      else dropped.push(ticket)
    })
    return {
      rows: buildNiiSheetRows(included),
      sheetTickets: included,
      skipped: dropped,
    }
  }, [tickets, buildSheetTicket])

  /**
   * Suppressed until units and sites resolve: they load after tickets do, so
   * on the first render every asset and site would look missing.
   */
  const missingIdentity = useMemo(
    () =>
      unitsLoading || sitesLoading || signoffsLoading
        ? { assetId: [], site: [] }
        : findMissingTicketIdentity(sheetTickets),
    [unitsLoading, sitesLoading, signoffsLoading, sheetTickets]
  )

  const columnsBySheet = useMemo<NiiSheetColumns>(
    () => ({
      tickets: rows.tickets.headers,
      zones: rows.zones.headers,
      history: rows.history.headers,
    }),
    [rows]
  )

  const generateAndDownload =
    useCallback(async (): Promise<NiiSheetExportSummary> => {
      setIsGenerating(true)
      try {
        const selected = applyNiiSheetSelection(rows, selection)
        const hasAnyColumns = [
          selected.tickets,
          selected.zones,
          selected.history,
        ].some((sheet) => sheet.headers.length > 0)

        if (sheetTickets.length === 0 || !hasAnyColumns) {
          return { exportedCount: 0, skipped }
        }

        const blob = await buildNiiSheetWorkbook(selected)
        downloadBlob(blob, buildNiiSheetFilename(sheetTickets.length))
        return { exportedCount: sheetTickets.length, skipped }
      } finally {
        setIsGenerating(false)
      }
    }, [rows, selection, sheetTickets.length, skipped])

  return {
    generateAndDownload,
    isGenerating,
    isLoading: signoffsLoading,
    columnsBySheet,
    missingIdentity,
  }
}
